"use client";

import { useSession } from "next-auth/react";
import { useEffect } from "react";
import { clearAnonymousSession, getAnonymousSession } from "@/lib/anonymousSession";

/**
 * Hook to automatically claim temporary documents when user signs in
 * Should be used in the root layout or a high-level component
 */
export function useClaimTemporaryDocuments() {
  const { data: session, status } = useSession();

  useEffect(() => {
    // Only run when session is loaded and user is authenticated
    if (status !== "authenticated" || !session?.user?.email) {
      return;
    }

    const claimDocuments = async () => {
      // Check if there's an anonymous session with potential temporary documents
      const anonymousSession = getAnonymousSession();

      if (!anonymousSession) {
        return;
      }

      try {
        const response = await fetch("/api/documents/claim-temporary/create", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            anonymousSessionId: anonymousSession.sessionId,
          }),
        });

        if (response.ok) {
          const data = await response.json();

          if (data.claimedCount > 0) {
            // Show a notification to the user (optional)
            if (typeof window !== "undefined" && data.documents) {
              // You can integrate with your notification system here
              console.log("📋 Claimed documents:", data.documents);
            }
          } else {
            console.log("ℹ️  No temporary documents to claim");
          }

          // Clear the anonymous session after successful claim
          clearAnonymousSession();
        } else {
          const error = await response.json();
          console.error("❌ Failed to claim temporary documents:", error);
        }
      } catch (error) {
        console.error("❌ Error claiming temporary documents:", error);
      }
    };

    // Run the claim process
    claimDocuments();
  }, [session, status]);
}
